import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { TabPanel, Tabs, useTabInUrl } from '../../components/Tabs.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  issueFor,
  Loading,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';

/** Resposta do servidor sobre o sigilo de pessoal (ADR 0049, R14). */
export interface PayrollView {
  partial: boolean;
}

/**
 * Aviso de visão parcial: a pessoa não vê os valores de pessoal, então totais, diferenças e
 * painéis não representam a DRE completa.
 */
export function PartialPayrollNotice({ payroll }: { payroll?: PayrollView | null }) {
  const { t } = useTranslation();
  if (!payroll?.partial) return null;
  return (
    <p role="note" className="rounded-lg border border-info/30 bg-info/5 p-3 text-sm">
      <strong>{t('fpaPayroll.partial')}</strong> {t('fpaPayroll.partialHint')}
    </p>
  );
}

interface Rule {
  id: string;
  classificationId: string;
  name: string;
  category: 'TIPO-GASTO' | 'TIPO-CLASSIFICACAO';
  reason: string;
  isActive: boolean;
  updatedAt: string;
  updatedByName: string | null;
}
interface Access {
  id: number;
  occurredAt: string;
  userName: string | null;
  operation: string;
  context: Record<string, unknown>;
}
interface PayrollList {
  rules: Rule[];
  options: { id: string; name: string; category: Rule['category'] }[];
  access: Access[];
  actions: { manage: boolean };
}

const TABS = ['rules', 'access'] as const;

/**
 * Lista de pessoal do FP&A (ADR 0049, R14): o que é valor de pessoal. Quem não tem a permissão de
 * pessoal não vê esses valores em nenhuma tela, total ou exportação. Toda mudança leva o motivo.
 */
export function FpaPayrollPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useTabInUrl(TABS, 'rules');
  const [adding, setAdding] = useState<{ classificationId: string; reason: string } | null>(null);
  const [toggling, setToggling] = useState<{ rule: Rule; reason: string } | null>(null);
  const data = useQuery({
    queryKey: ['fpa', 'payroll'],
    queryFn: () => api.get<PayrollList>('/fpa/payroll'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa'] });
  const add = useMutation({
    mutationFn: (body: NonNullable<typeof adding>) => api.post('/fpa/payroll/rules', body),
    onSuccess: async () => {
      setAdding(null);
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ rule, reason }: NonNullable<typeof toggling>) =>
      api.post(`/fpa/payroll/rules/${rule.id}/status`, { isActive: !rule.isActive, reason }),
    onSuccess: async () => {
      setToggling(null);
      await refresh();
    },
  });
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const { rules, options, access, actions } = data.data;
  const category = (c: Rule['category']) => t(`fpaPayroll.categories.${c}`);

  return (
    <>
      <PageHeader
        title={t('nav.fpaPayroll')}
        description={t('fpaPayroll.description')}
        actions={
          actions.manage ? (
            <Button
              disabled={options.length === 0}
              onClick={() => {
                add.reset();
                setAdding({ classificationId: '', reason: '' });
              }}
            >
              {t('fpaPayroll.add')}
            </Button>
          ) : null
        }
      />

      <FormModal
        open={!!adding}
        size="md"
        title={t('fpaPayroll.add')}
        description={t('fpaPayroll.addHint')}
        dirty={!!adding && (!!adding.classificationId || !!adding.reason)}
        saving={add.isPending}
        error={add.error}
        onSave={() => adding && add.mutate(adding)}
        onClose={() => setAdding(null)}
      >
        {adding && (
          <FormSection columns={1}>
            <Field label={t('fpaPayroll.item')} required>
              <Select
                required
                value={adding.classificationId}
                onChange={(ev) => setAdding({ ...adding, classificationId: ev.target.value })}
              >
                <option value="">{t('common.choose')}</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {category(o.category)} · {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fpaPayroll.reason')} error={issueFor(add.error, 'reason')} required>
              <Textarea
                required
                maxLength={1000}
                value={adding.reason}
                onChange={(ev) => setAdding({ ...adding, reason: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!toggling}
        size="md"
        title={t(toggling?.rule.isActive ? 'fpaPayroll.deactivate' : 'fpaPayroll.activate')}
        description={t(
          toggling?.rule.isActive ? 'fpaPayroll.deactivateHint' : 'fpaPayroll.activateHint',
        )}
        dirty={!!toggling?.reason}
        saving={toggle.isPending}
        error={toggle.error}
        onSave={() => toggling && toggle.mutate(toggling)}
        onClose={() => setToggling(null)}
      >
        {toggling && (
          <FormSection columns={1}>
            <Field label={t('fpaPayroll.reason')} error={issueFor(toggle.error, 'reason')} required>
              <Textarea
                required
                maxLength={1000}
                value={toggling.reason}
                onChange={(ev) => setToggling({ ...toggling, reason: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <div className="border-b border-rule">
        <Tabs
          items={TABS.map((k) => ({
            id: k,
            label: t(`fpaPayroll.tabs.${k}`),
            count: k === 'rules' ? rules.filter((r) => r.isActive).length : undefined,
          }))}
          value={tab}
          onChange={setTab}
          label={t('nav.fpaPayroll')}
          idPrefix="fpa-payroll"
        />
      </div>

      {tab === 'rules' && (
        <TabPanel idPrefix="fpa-payroll" id="rules">
          <Table
            head={[
              t('fpaPayroll.kind'),
              t('fpaPayroll.item'),
              t('fpaPayroll.reason'),
              t('fields.status'),
              '',
            ]}
          >
            {rules.length === 0 && <EmptyRow colSpan={5}>{t('fpaPayroll.empty')}</EmptyRow>}
            {rules.map((r) => (
              <tr key={r.id}>
                <td className="text-xs">{category(r.category)}</td>
                <td>{r.name}</td>
                <td className="text-xs">
                  {r.reason}
                  <div className="text-muted">
                    {[r.updatedByName, when.format(new Date(r.updatedAt))]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </td>
                <td>
                  <Badge tone={r.isActive ? 'ok' : 'off'}>
                    {t(r.isActive ? 'fpaPayroll.protected' : 'fields.inactive')}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    {actions.manage && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          toggle.reset();
                          setToggling({ rule: r, reason: '' });
                        }}
                      >
                        {t(r.isActive ? 'fpaPayroll.deactivate' : 'fpaPayroll.activate')}
                      </Button>
                    )}
                    <MasterDeleteButton table="fpa.payroll_rule" id={r.id} compact />
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        </TabPanel>
      )}

      {tab === 'access' && (
        <TabPanel idPrefix="fpa-payroll" id="access">
          <p className="text-sm text-muted">{t('fpaPayroll.accessHint')}</p>
          <Table head={[t('fpaPayroll.when'), t('fpaPayroll.who'), t('fpaPayroll.operation')]}>
            {access.length === 0 && <EmptyRow colSpan={3}>{t('fpaPayroll.accessEmpty')}</EmptyRow>}
            {access.map((a) => (
              <tr key={a.id}>
                <td className="text-xs tabular-nums">{when.format(new Date(a.occurredAt))}</td>
                <td className="text-sm">{a.userName ?? '—'}</td>
                <td className="text-sm">{t(`fpaPayroll.operations.${a.operation}`)}</td>
              </tr>
            ))}
          </Table>
        </TabPanel>
      )}
    </>
  );
}
